import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "../components/ThemeContext";
import { Table, Spinner, Alert, Pagination } from "react-bootstrap";
import CanvasJSReact from "@canvasjs/react-charts";
import NavMenu from "../components/NavMenu";
import axios from "axios";
import { FaFileExcel, FaFileWord } from "react-icons/fa";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  Document,
  Packer,
  Paragraph,
  Table as DocxTable,
  TableRow,
  TableCell,
  WidthType,
  TextRun,
} from "docx";

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

function Reports() {
  const [staffData, setStaffData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedAppraisal, setSelectedAppraisal] = useState("");

  const [appraisalNames, setAppraisalNames] = useState([]);

  const [error, setError] = useState("");
  const { isDark } = useTheme();
  const [part1Rows, setPart1Rows] = useState([]);
  const [loadingPart1, setLoadingPart1] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const departmentsPerPage = 5;

  const safeText = (v) => (v == null ? "" : String(v));

  const rowDate = (row) => {
    const candidates = [
      row.updated_at,
      row.modified_at,
      row.submitted_at,
      row.created_at,
      row.send_date,
      row.sent_at,
      row.appraisal_date,
      row.appraisal_year,
    ].filter(Boolean);

    if (
      candidates.length === 1 &&
      String(candidates[0]).match(/^\d{4}$/) &&
      Number(candidates[0]) > 1900
    ) {
      return new Date(`${candidates[0]}-12-31T23:59:59`);
    }

    for (const c of candidates) {
      const d = new Date(c);
      if (!Number.isNaN(d.getTime())) return d;
    }
    return null;
  };

  const computeLatestAppraisalName = (rows) => {
    const map = new Map();
    rows.forEach((r) => {
      const name = safeText(r.appraisal_name).trim();
      if (!name) return;
      const d = rowDate(r);
      const cur = map.get(name);

      if (d) {
        if (!cur || (cur && d > cur)) map.set(name, d);
      } else {
        if (!cur) map.set(name, null);
      }
    });

    const sorted = Array.from(map.entries()).sort((a, b) => {
      const da = a[1];
      const db = b[1];
      if (da && db) return db - da;
      if (da && !db) return -1;
      if (!da && db) return 1;
      return b[0].localeCompare(a[0]);
    });

    return {
      latestName: sorted[0]?.[0] || "",
      sortedNames: sorted.map(([name]) => name),
    };
  };

  useEffect(() => {
    const fetchPart1 = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      try {
        setLoadingPart1(true);
        const res = await axios.get(`${process.env.REACT_APP_API}/getPart1`, {
          headers: { Authorization: `Bearer ${token}` },
          params: selectedAppraisal ? { appraisal_name: selectedAppraisal } : {},
        });

        if (!res.data?.success) throw new Error("Failed to fetch Part I data");
        setPart1Rows(res.data.data || []);
      } catch (err) {
        console.error("Part I fetch error:", err.message);
        setPart1Rows([]);
      } finally {
        setLoadingPart1(false);
      }
    };

    fetchPart1();
  }, [selectedAppraisal]);

  useEffect(() => {
    const fetchAppraisalData = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        setError("No auth token found in localStorage");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}/getAllAppraisal`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          const rows = response.data.data || [];
          setStaffData(rows);

          const { latestName, sortedNames } = computeLatestAppraisalName(rows);
          setAppraisalNames(sortedNames);

          setSelectedAppraisal(latestName || "");
        } else {
          setError("Failed to fetch data.");
        }
      } catch (err) {
        setError("Error fetching data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAppraisalData();
  }, []);

  const filteredStaffData = useMemo(() => {
    if (!selectedAppraisal) return staffData;
    return staffData.filter((emp) => emp.appraisal_name === selectedAppraisal);
  }, [staffData, selectedAppraisal]);

  const part1Visible = part1Rows;

  const getRandomColor = () => {
    const colors = [
      "#e57373",
      "#64b5f6",
      "#81c784",
      "#ffd54f",
      "#ba68c8",
      "#4db6ac",
      "#ff8a65",
      "#7986cb",
      "#a1887f",
      "#90caf9",
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const chartOptions = useMemo(
    () => ({
      animationEnabled: true,
      backgroundColor: "transparent",
      title: {
        text: "Staff Ratings",
        fontSize: 16,
        fontFamily: "Arimo-Regular",
        fontColor: isDark ? "#ffffff" : "#000000",
      },
      axisY: {
        title: "Rating",
        includeZero: true,
        maximum: 5,
        labelFontColor: isDark ? "#ffffff" : "#000000",
        titleFontColor: isDark ? "#ffffff" : "#000000",
        labelFontFamily: "Arimo-Regular",
        titleFontFamily: "Arimo-Regular",
      },
      axisX: {
        title: "Employee",
        labelAngle: -45,
        interval: 1,
        labelFontSize: 10,
        labelFontColor: isDark ? "#ffffff" : "#000000",
        titleFontColor: isDark ? "#ffffff" : "#000000",
        labelFontFamily: "Arimo-Regular",
        titleFontFamily: "Arimo-Regular",
      },
      data: [
        {
          type: "column",
          dataPoints: filteredStaffData.map((emp) => ({
            label: emp.staff_name,
            y: parseFloat(emp.overall_result ?? 0),
            color: getRandomColor(),
          })),
        },
      ],
    }),
    [filteredStaffData, isDark]
  );

  const user = JSON.parse(localStorage.getItem("user"));
  const userDescription = user?.description || "";

  if (userDescription !== "HR" && userDescription !== "MIS") {
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <h1>Unauthorized</h1>
        <p>You do not have permission to view this page.</p>
      </div>
    );
  }

  const renderGroupedTable = () => {
    const departmentMap = {};
    filteredStaffData.forEach((emp) => {
      const dept = emp.department || "No Department";
      if (!departmentMap[dept]) departmentMap[dept] = [];
      departmentMap[dept].push(emp);
    });

    const departments = Object.keys(departmentMap);
    const totalPages = Math.ceil(departments.length / departmentsPerPage);

    const paginatedDepartments = departments.slice(
      (currentPage - 1) * departmentsPerPage,
      currentPage * departmentsPerPage
    );

    return (
      <>
        <Table
          bordered
          responsive
          hover
          size="sm"
          className={`mt-2 mb-3 ${isDark ? "table-dark text-light" : ""}`}
        >
          <thead>
            <tr className="text-center">
              <th>No</th>
              <th>Employee Name</th>
              <th>Supervisor</th>
              <th>Part I</th>
              <th>Part II</th>
              <th>Overall Rating</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {paginatedDepartments.map((department, deptIdx) => (
              <React.Fragment key={deptIdx}>
                <tr
                  className={
                    isDark ? "bg-secondary text-light" : "table-light text-dark"
                  }
                >
                  <td colSpan={7} className="fw-bold text-start">
                    {department}
                  </td>
                </tr>
                {departmentMap[department].map((emp, index) => (
                  <tr key={emp.send_id || `${department}-${index}`}>
                    <td className="text-center">{index + 1}</td>
                    <td className="text-start">{emp.staff_name}</td>
                    <td className="text-center">{emp.supervisor_name}</td>
                    <td className="text-center">
                      <span
                        style={{
                          color:
                            (emp.status || "").toLowerCase() === "submitted"
                              ? "green"
                              : undefined,
                          fontWeight:
                            (emp.status || "").toLowerCase() === "submitted"
                              ? "bold"
                              : undefined,
                        }}
                      >
                        {emp.part1_result}
                      </span>
                    </td>
                    <td className="text-center">
                      <span
                        style={{
                          color:
                            (emp.status || "").toLowerCase() === "submitted"
                              ? "green"
                              : undefined,
                          fontWeight:
                            (emp.status || "").toLowerCase() === "submitted"
                              ? "bold"
                              : undefined,
                        }}
                      >
                        {emp.part2_result}
                      </span>
                    </td>
                    <td className="text-center">
                      <span
                        style={{
                          color:
                            (emp.status || "").toLowerCase() === "submitted"
                              ? "green"
                              : undefined,
                          fontWeight:
                            (emp.status || "").toLowerCase() === "submitted"
                              ? "bold"
                              : undefined,
                        }}
                      >
                        {emp.overall_result ?? "-"}
                      </span>
                    </td>
                    <td className="text-center" style={{ fontSize: "13px" }}>
                      {emp.status ?? "-"}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </Table>

        {totalPages > 1 && (
          <Pagination className="justify-content-center mt-2">
            <Pagination.First
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            />
            {Array.from({ length: totalPages }, (_, idx) => (
              <Pagination.Item
                key={idx + 1}
                active={currentPage === idx + 1}
                onClick={() => setCurrentPage(idx + 1)}
              >
                {idx + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        )}

        <small className="text-secondary">
          * Submitted appraisal ratings are shown in green color
        </small>
      </>
    );
  };

  // ---------- Export helpers ----------
  const fmtScore = (v) =>
    v === null || v === undefined || v === "" || isNaN(Number(v))
      ? "-"
      : Number(v).toFixed(2);

  // 1) All Staff Ratings by Departments
  const exportGroupedAllStaffToExcel = (
    data,
    filename = "All_Staff_Ratings_By_Department.xlsx"
  ) => {
    const deptMap = {};
    data.forEach((d) => {
      const dept = d.department || "No Department";
      (deptMap[dept] ||= []).push(d);
    });

    const aoa = [];
    const merges = [];

    aoa.push(["", "Ratings", "", ""]);
    merges.push({ s: { r: 0, c: 1 }, e: { r: 0, c: 3 } });
    aoa.push(["", "Part 1", "Part 2", "Overall"]);

    Object.entries(deptMap).forEach(([dept, rows], i, arr) => {
      aoa.push([dept, "", "", ""]);
      rows.forEach((emp) => {
        aoa.push([
          emp.staff_name || "",
          fmtScore(emp.part1_result),
          fmtScore(emp.part2_result),
          fmtScore(emp.overall_result),
        ]);
      });
      if (i < arr.length - 1) aoa.push(["", "", "", ""]);
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!merges"] = merges;
    ws["!cols"] = [{ wch: 32 }, { wch: 10 }, { wch: 10 }, { wch: 10 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Overall by Dept");
    const wbout = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    saveAs(
      new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      }),
      filename
    );
  };

  const exportGroupedAllStaffToWord = async (
    data,
    filename = "All_Staff_Ratings_By_Department.docx"
  ) => {
    const deptMap = {};
    data.forEach((d) => {
      const dept = d.department || "No Department";
      (deptMap[dept] ||= []).push(d);
    });

    const rows = [];

    rows.push(
      new TableRow({
        children: [
          new TableCell({
            width: { size: 25, type: WidthType.PERCENTAGE },
            children: [new Paragraph("")],
          }),
          new TableCell({
            columnSpan: 3,
            width: { size: 75, type: WidthType.PERCENTAGE },
            children: [
              new Paragraph({
                children: [new TextRun({ text: "Ratings", bold: true })],
              }),
            ],
          }),
        ],
      })
    );

    rows.push(
      new TableRow({
        children: [
          new TableCell({
            width: { size: 25, type: WidthType.PERCENTAGE },
            children: [new Paragraph("")],
          }),
          ...["Part 1", "Part 2", "Overall"].map(
            (label) =>
              new TableCell({
                width: { size: 25, type: WidthType.PERCENTAGE },
                children: [
                  new Paragraph({
                    children: [new TextRun({ text: label, bold: true })],
                  }),
                ],
              })
          ),
        ],
      })
    );

    Object.entries(deptMap).forEach(([dept, employees], i, arr) => {
      rows.push(
        new TableRow({
          children: [
            new TableCell({
              width: { size: 25, type: WidthType.PERCENTAGE },
              children: [
                new Paragraph({
                  children: [new TextRun({ text: dept, bold: true })],
                }),
              ],
            }),
            new TableCell({ children: [new Paragraph("")] }),
            new TableCell({ children: [new Paragraph("")] }),
            new TableCell({ children: [new Paragraph("")] }),
          ],
        })
      );

      employees.forEach((emp) => {
        rows.push(
          new TableRow({
            children: [
              new TableCell({
                children: [
                  new Paragraph(emp.staff_name ? String(emp.staff_name) : ""),
                ],
              }),
              new TableCell({
                children: [new Paragraph(fmtScore(emp.part1_result))],
              }),
              new TableCell({
                children: [new Paragraph(fmtScore(emp.part2_result))],
              }),
              new TableCell({
                children: [new Paragraph(fmtScore(emp.overall_result))],
              }),
            ],
          })
        );
      });

      if (i < arr.length - 1) {
        rows.push(
          new TableRow({
            children: [
              new TableCell({ children: [new Paragraph("")] }),
              new TableCell({ children: [new Paragraph("")] }),
              new TableCell({ children: [new Paragraph("")] }),
              new TableCell({ children: [new Paragraph("")] }),
            ],
          })
        );
      }
    });

    const doc = new Document({
      sections: [
        {
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: "All Staff Ratings by Department",
                  bold: true,
                }),
              ],
            }),
            new Paragraph({ text: "" }),
            new DocxTable({
              width: { size: 100, type: WidthType.PERCENTAGE },
              rows,
            }),
          ],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, filename);
  };

  // 2) Overall Rating grouped by role bucket
  const getRoleGroup = (emp) => {
    if (emp.role_bucket) {
      const b = String(emp.role_bucket).toLowerCase();
      if (b.includes("executive")) return "Executives";
      if (b.includes("lectur")) return "Lecturers";
      if (b.includes("manager")) return "Managers";
      if (b.includes("director")) return "Directors";
      return null;
    }
    const level = Number(emp.position_level);
    const pos = (emp.position || "").toLowerCase();

    if (pos.includes("lectur") && [5, 6].includes(level)) return "Lecturers";
    if ([0, 1, 2].includes(level)) return "Directors";
    if ([3, 4].includes(level)) return "Managers";
    if ([5, 6].includes(level)) return "Executives";

    return null;
  };

  const toNum = (v) =>
    v == null || v === "" || isNaN(Number(v)) ? null : Number(v);

  const exportScoresByRoleExcel = (data, filename = "Scores_By_Role.xlsx") => {
    const CATS = ["Executives", "Lecturers", "Managers", "Directors"];
    const groups = Object.fromEntries(CATS.map((c) => [c, []]));

    data.forEach((emp) => {
      const g = getRoleGroup(emp);
      if (g && groups[g]) groups[g].push(emp);
    });

    const maxLen = Math.max(1, ...CATS.map((c) => groups[c].length));
    const colsPerGroup = 4;
    const totalCols = CATS.length * colsPerGroup;
    const totalRows = 1 + maxLen + 1;

    const aoa = Array.from({ length: totalRows }, () =>
      Array(totalCols).fill("")
    );

    CATS.forEach((cat, gi) => {
      const b = gi * colsPerGroup;
      aoa[0][b] = cat;
      aoa[0][b + 1] = "Part I";
      aoa[0][b + 2] = "Part II";
      aoa[0][b + 3] = "Overall";
    });

    CATS.forEach((cat, gi) => {
      const b = gi * colsPerGroup;
      const list = groups[cat];
      for (let ri = 0; ri < maxLen; ri++) {
        const emp = list[ri];
        if (!emp) continue;
        aoa[1 + ri][b] = emp.staff_name || "";
        aoa[1 + ri][b + 1] = toNum(emp.part1_result);
        aoa[1 + ri][b + 2] = toNum(emp.part2_result);
        aoa[1 + ri][b + 3] = toNum(emp.overall_result);
      }
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    const avgRowZero = 1 + maxLen;
    CATS.forEach((cat, gi) => {
      const b = gi * colsPerGroup;

      const labelAddr = XLSX.utils.encode_cell({ r: avgRowZero, c: b });
      ws[labelAddr] = { t: "s", v: "Average" };

      for (let k = 1; k <= 3; k++) {
        const startAddr = XLSX.utils.encode_cell({ r: 1, c: b + k });
        const endAddr = XLSX.utils.encode_cell({ r: maxLen, c: b + k });
        const avgAddr = XLSX.utils.encode_cell({ r: avgRowZero, c: b + k });
        ws[avgAddr] = { t: "n", f: `AVERAGE(${startAddr}:${endAddr})` };
      }
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "By Role");
    const wbout = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    saveAs(
      new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      }),
      filename
    );
  };

  const avg = (nums) => {
    const vals = nums.filter((n) => n != null);
    if (!vals.length) return "";
    const sum = vals.reduce((a, b) => a + b, 0);
    return (sum / vals.length).toFixed(2);
  };

  const exportScoresByRoleWord = async (
    data,
    filename = "Scores_By_Role.docx"
  ) => {
    const CATS = ["Executives", "Lecturers", "Managers", "Directors"];
    const groups = Object.fromEntries(CATS.map((c) => [c, []]));

    data.forEach((emp) => {
      const g = getRoleGroup(emp);
      if (g && groups[g]) groups[g].push(emp);
    });

    const maxLen = Math.max(1, ...CATS.map((c) => groups[c].length));
    const rows = [];

    rows.push(
      new TableRow({
        children: CATS.flatMap((cat) => [
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: cat, bold: true })],
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: "Part I", bold: true })],
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: "Part II", bold: true })],
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: "Overall", bold: true })],
              }),
            ],
          }),
        ]),
      })
    );

    for (let ri = 0; ri < maxLen; ri++) {
      rows.push(
        new TableRow({
          children: CATS.flatMap((cat) => {
            const emp = groups[cat][ri];
            if (!emp) {
              return [
                new TableCell({ children: [new Paragraph("")] }),
                new TableCell({ children: [new Paragraph("")] }),
                new TableCell({ children: [new Paragraph("")] }),
                new TableCell({ children: [new Paragraph("")] }),
              ];
            }
            return [
              new TableCell({
                children: [new Paragraph(emp.staff_name || "")],
              }),
              new TableCell({
                children: [new Paragraph(fmtScore(toNum(emp.part1_result)))],
              }),
              new TableCell({
                children: [new Paragraph(fmtScore(toNum(emp.part2_result)))],
              }),
              new TableCell({
                children: [new Paragraph(fmtScore(toNum(emp.overall_result)))],
              }),
            ];
          }),
        })
      );
    }

    rows.push(
      new TableRow({
        children: CATS.flatMap((cat) => {
          const g = groups[cat];
          const part1 = g.map((e) => toNum(e.part1_result));
          const part2 = g.map((e) => toNum(e.part2_result));
          const overall = g.map((e) => toNum(e.overall_result));
          return [
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: "Average", bold: true })],
                }),
              ],
            }),
            new TableCell({ children: [new Paragraph(avg(part1))] }),
            new TableCell({ children: [new Paragraph(avg(part2))] }),
            new TableCell({ children: [new Paragraph(avg(overall))] }),
          ];
        }),
      })
    );

    const doc = new Document({
      sections: [
        {
          children: [
            new Paragraph({
              children: [new TextRun({ text: "Scores by Role", bold: true })],
            }),
            new Paragraph(""),
            new DocxTable({ rows }),
          ],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, filename);
  };

  // 3) Rating by Elements (Excel)
  const cleanFactor = (s) =>
    (s || "")
      .toString()
      .replace(/\s*\([^)]*\)\s*/g, "")
      .trim();
  const norm = (s) => cleanFactor(s).toUpperCase();

  const ROLE_FACTORS = {};

  const getFactorsForRole = (rows, roleName) => {
    const canonical = ROLE_FACTORS[roleName];
    if (Array.isArray(canonical) && canonical.length) {
      return canonical.map(cleanFactor);
    }

    const seen = new Set();
    const out = [];
    rows.forEach((r) => {
      const role =
        (r.role_bucket || r.role_title || "Others").toString().trim() ||
        "Others";
      if (role !== roleName) return;
      const fac = cleanFactor(r.factor);
      const key = norm(fac);
      if (fac && !seen.has(key)) {
        seen.add(key);
        out.push(fac);
      }
    });
    return out.slice(0, 10);
  };

  const pivotByRole = (rows) => {
    const book = new Map(); // role => Map(emp => Map(factor => number))
    rows.forEach((r) => {
      const role =
        (r.role_bucket || r.role_title || "Others").toString().trim() ||
        "Others";
      const emp = (r.display_name || r.staff_name || "").toString().trim();
      if (!role || !emp) return;

      const fac = cleanFactor(r.factor);
      if (!book.has(role)) book.set(role, new Map());
      const roleMap = book.get(role);

      if (!roleMap.has(emp)) roleMap.set(emp, new Map());
      const facMap = roleMap.get(emp);

      const rat =
        r.rating == null || r.rating === "" || isNaN(Number(r.rating))
          ? null
          : Number(r.rating);

      if (fac && rat != null) facMap.set(fac, rat);
    });
    return book;
  };

  const ROLE_TABS = ["Executives", "Lecturers", "Managers", "Directors", "Others"];

  const exportElementsToExcel = (
    rows,
    filename = "Rating_By_Elements.xlsx"
  ) => {
    const byRole = pivotByRole(rows);
    const wb = XLSX.utils.book_new();

    ROLE_TABS.forEach((roleName) => {
      const empMap = byRole.get(roleName) || new Map();
      const FACTORS_ORDER = getFactorsForRole(rows, roleName);

      const aoa = [];
      aoa.push(["", ...FACTORS_ORDER]);
      aoa.unshift(["", ...Array(FACTORS_ORDER.length).fill("")]);

      const employees = Array.from(empMap.keys()).sort((a, b) =>
        a.localeCompare(b)
      );

      employees.forEach((emp) => {
        const facMap = empMap.get(emp);
        const row = [emp, ...FACTORS_ORDER.map((f) => facMap.get(f) ?? "")];
        aoa.push(row);
      });

      const avgRow = ["Average"];
      for (let ci = 1; ci <= FACTORS_ORDER.length; ci++) {
        const startAddr = XLSX.utils.encode_cell({ r: 2, c: ci });
        const endAddr = XLSX.utils.encode_cell({
          r: 1 + employees.length,
          c: ci,
        });
        avgRow.push({ t: "n", f: `IFERROR(AVERAGE(${startAddr}:${endAddr}), "")` });
      }
      aoa.push(avgRow);

      const ws = XLSX.utils.aoa_to_sheet(aoa);

      ws["!merges"] = ws["!merges"] || [];
      if (FACTORS_ORDER.length > 0) {
        ws["!merges"].push({
          s: { r: 0, c: 1 },
          e: { r: 0, c: FACTORS_ORDER.length },
        });
        ws[XLSX.utils.encode_cell({ r: 0, c: 1 })] = { t: "s", v: roleName };
      }

      ws[XLSX.utils.encode_cell({ r: 1, c: 0 })] = { t: "s", v: "" };
      FACTORS_ORDER.forEach((f, i) => {
        ws[XLSX.utils.encode_cell({ r: 1, c: 1 + i })] = { t: "s", v: f };
      });

      ws["!cols"] = [{ wch: 28 }, ...FACTORS_ORDER.map(() => ({ wch: 20 }))];

      XLSX.utils.book_append_sheet(wb, ws, roleName);
    });

    const wbout = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    saveAs(
      new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      }),
      filename
    );
  };

  // 4) Training Needs Analysis (Word)
  const uniqueStaffForPart3 = (rows) => {
    const byStaff = new Map();

    rows.forEach((r) => {
      const id = r.staff_id ?? r.emp_no ?? r.empId ?? null;
      const name = (r.display_name || r.staff_name || "").trim();
      if (!name) return;

      const key = id ? `id:${id}` : `name:${name.toLowerCase()}`;
      const c3 = (r.comment_3 ?? r.comment3 ?? "").toString().trim();

      if (!byStaff.has(key)) {
        byStaff.set(key, { name, comment3: c3 });
      } else {
        const cur = byStaff.get(key);
        if (!cur.comment3 && c3) cur.comment3 = c3;
        else if (c3 && c3.length > (cur.comment3 || "").length) cur.comment3 = c3;
      }
    });

    return Array.from(byStaff.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  };

  const exportTrainingNeedsWord = async (
    rows,
    titleText = `ANALYSIS OF PERFORMANCE APPRAISAL ${
      selectedAppraisal || ""
    }: Qualitative Comments`,
    filename = "Training_Needs_Analysis.docx",
    onlyWithComments = false
  ) => {
    let staff = uniqueStaffForPart3(rows);
    if (onlyWithComments)
      staff = staff.filter((s) => (s.comment3 || "").trim().length);

    const headerRow = new TableRow({
      children: [
        new TableCell({ children: [new Paragraph("Area")] }),
        new TableCell({ children: [new Paragraph("Name of Staff")] }),
      ],
    });

    const bodyRows = (staff.length ? staff : [{ name: "", comment3: "" }]).map(
      (s) =>
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph(s.comment3 || "")] }),
            new TableCell({ children: [new Paragraph(s.name || "")] }),
          ],
        })
    );

    const table = new DocxTable({ rows: [headerRow, ...bodyRows] });

    const doc = new Document({
      sections: [
        {
          children: [
            new Paragraph({
              children: [new TextRun({ text: titleText, bold: true })],
            }),
            new Paragraph(""),
            table,
          ],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, filename);
  };

  // ---------- Click handlers ----------
  const handleDownloadOverallExcel = () =>
    exportGroupedAllStaffToExcel(filteredStaffData);

  const handleDownloadOverallWord = () =>
    exportGroupedAllStaffToWord(filteredStaffData);

  const handleDownloadByRoleExcel = () =>
    exportScoresByRoleExcel(filteredStaffData);

  const handleDownloadByRoleWord = () =>
    exportScoresByRoleWord(filteredStaffData);

  const handleDownloadElementsExcel = () => exportElementsToExcel(part1Visible);

  const handleDownloadTrainingWord = () => {
    exportTrainingNeedsWord(part1Visible);
  };

  return (
    <>
      <NavMenu />
      <div className={`container-fluid p-4 ${isDark ? "text-light" : "text-dark"}`}>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h5 className="header-title fw-bold mb-0">Reporting & Analysis</h5>

          {/* ✅ latest appraisal first + default selected = latest */}
          <select
            className={`form-select form-select-sm w-auto ${
              isDark ? "bg-dark text-light border-secondary" : ""
            }`}
            value={selectedAppraisal}
            onChange={(e) => {
              setSelectedAppraisal(e.target.value);
              setCurrentPage(1);
            }}
          >
            {/* ✅ remove "All Appraisals" option if you don't want it at all */}
            {/* <option value="">All Appraisals</option> */}

            {appraisalNames.map((name, idx) => (
              <option key={idx} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="text-center my-5">
            <Spinner animation="border" variant={isDark ? "light" : "primary"} />
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : (
          <div className="row g-4 align-items-stretch" style={{ minHeight: "80vh" }}>
            <div className="col-md-4">
              <div
                className={`p-3 rounded shadow-sm h-100 d-flex flex-column ${
                  isDark ? "bg-dark text-light" : "bg-light text-dark"
                }`}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="fw-bold mb-0">Ranking</h6>
                </div>
                <div className="flex-grow-1 overflow-auto">{renderGroupedTable()}</div>
              </div>
            </div>

            <div className="col-md-8 d-flex flex-column" style={{ height: "100%" }}>
              <div className="d-flex flex-column gap-4 h-100">
                <div
                  className={`p-3 rounded shadow-sm flex-fill d-flex flex-column ${
                    isDark ? "bg-dark text-light" : "bg-light text-dark"
                  }`}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="fw-bold mb-0">Analysis BarChart</h6>
                  </div>
                  <div className="flex-grow-1">
                    <CanvasJSChart options={chartOptions} />
                  </div>
                </div>

                <div
                  className={`p-3 rounded shadow-sm flex-fill d-flex flex-column ${
                    isDark ? "bg-dark text-light" : "bg-light text-dark"
                  }`}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="fw-bold mb-0">Report Printing</h6>
                  </div>

                  <div className="row g-3 row-cols-1 row-cols-sm-2 mt-2">
                    <div className="col">
                      <div
                        className={`p-3 rounded shadow-sm h-100 ${
                          isDark
                            ? "bg-dark text-light border border-secondary"
                            : "bg-white"
                        }`}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 className="fw-bold mb-1">All Staff Ratings</h6>
                            <small className="text-secondary">
                              Individual appraisal scores for every staff grouped by department
                            </small>
                          </div>
                        </div>
                        <div className="d-flex gap-2 mt-3">
                          <button
                            className={`btn btn-sm d-flex align-items-center gap-2 ${
                              isDark ? "btn-outline-light" : "btn-outline-success"
                            }`}
                            onClick={handleDownloadOverallExcel}
                          >
                            <FaFileExcel /> Excel
                          </button>
                          <button
                            className={`btn btn-sm d-flex align-items-center gap-2 ${
                              isDark ? "btn-outline-light" : "btn-outline-primary"
                            }`}
                            onClick={handleDownloadOverallWord}
                          >
                            <FaFileWord /> Word
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="col">
                      <div
                        className={`p-3 rounded shadow-sm h-100 ${
                          isDark
                            ? "bg-dark text-light border border-secondary"
                            : "bg-white"
                        }`}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 className="fw-bold mb-1">Overall Rating</h6>
                            <small className="text-secondary">
                              Average Part I, Part II, and overall scores grouped by Executives,
                              Managers, Lecturers and Directors
                            </small>
                          </div>
                        </div>
                        <div className="d-flex gap-2 mt-3">
                          <button
                            className={`btn btn-sm d-flex align-items-center gap-2 ${
                              isDark ? "btn-outline-light" : "btn-outline-success"
                            }`}
                            onClick={handleDownloadByRoleExcel}
                          >
                            <FaFileExcel /> Excel
                          </button>
                          <button
                            className={`btn btn-sm d-flex align-items-center gap-2 ${
                              isDark ? "btn-outline-light" : "btn-outline-primary"
                            }`}
                            onClick={handleDownloadByRoleWord}
                          >
                            <FaFileWord /> Word
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="col">
                      <div
                        className={`p-3 rounded shadow-sm h-100 ${
                          isDark
                            ? "bg-dark text-light border border-secondary"
                            : "bg-white"
                        }`}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 className="fw-bold mb-1">Rating by Elements</h6>
                            <small className="text-secondary">
                              Detailed breakdown of ratings for appraisal Part I factors
                            </small>
                          </div>
                        </div>
                        <div className="d-flex gap-2 mt-3">
                          <button
                            className={`btn btn-sm d-flex align-items-center gap-2 ${
                              isDark ? "btn-outline-light" : "btn-outline-success"
                            }`}
                            onClick={handleDownloadElementsExcel}
                          >
                            <FaFileExcel /> Excel
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="col">
                      <div
                        className={`p-3 rounded shadow-sm h-100 ${
                          isDark
                            ? "bg-dark text-light border border-secondary"
                            : "bg-white"
                        }`}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 className="fw-bold mb-1">Training Needs Analysis</h6>
                            <small className="text-secondary">
                              Part III: List competencies to develop and training needs
                            </small>
                          </div>
                        </div>
                        <div className="d-flex gap-2 mt-3">
                          <button
                            className={`btn btn-sm d-flex align-items-center gap-2 ${
                              isDark ? "btn-outline-light" : "btn-outline-primary"
                            }`}
                            onClick={handleDownloadTrainingWord}
                            disabled={loadingPart1}
                          >
                            <FaFileWord /> Word
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>

          </div>
        )}
      </div>
    </>
  );
}

export default Reports;
